import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { BRAND } from "../data/content";
import { useI18n } from "../context/I18nContext";

const Footer = () => {
  const { t } = useI18n();
  const wa = `https://wa.me/${BRAND.phoneDigits}?text=${encodeURIComponent(
    BRAND.whatsappMessage
  )}`;

  const links = [
    { to: "/about", label: t("footer_about"), testid: "footer-about" },
    { to: "/contact", label: t("footer_contact"), testid: "footer-contact" },
    { to: "/track-order", label: t("footer_track"), testid: "footer-track" },
    { to: "/privacy-policy", label: t("footer_privacy"), testid: "footer-privacy" },
    { to: "/returns-refunds", label: t("footer_refund"), testid: "footer-refund" },
    { to: "/terms-conditions", label: t("footer_terms"), testid: "footer-terms" },
  ];

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#2C3E50] text-[#FFFBF5] mt-16 pb-24 sm:pb-12"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D63031] flex items-center justify-center font-display font-bold">
              G
            </div>
            <div>
              <div className="font-display font-bold text-lg text-white">
                Gruham
              </div>
              <div className="text-xs uppercase tracking-widest text-[#F39C12]">
                {BRAND.tagline}
              </div>
            </div>
          </div>
          <p className="mt-4 text-sm text-[#FFFBF5]/80 leading-relaxed">
            Affordable, practical comfort designed with real Indian moms.
          </p>

          <a
            data-testid="footer-whatsapp-link"
            href={wa}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-4 py-2 text-sm font-semibold hover:opacity-95 transition"
          >
            <MessageCircle className="w-4 h-4" />
            {t("footer_whatsapp")}
          </a>
        </div>

        <div>
          <div className="font-display font-semibold text-white mb-4">
            {t("language") === "ભાષા" ? "લિંક્સ" : "Links"}
          </div>
          <ul className="grid grid-cols-2 sm:grid-cols-1 gap-2 text-sm text-[#FFFBF5]/85">
            {links.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={l.testid}
                  className="hover:text-[#F39C12]"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="font-display font-semibold text-white mb-4">
            {t("footer_contact")}
          </div>
          <ul className="space-y-3 text-sm text-[#FFFBF5]/85">
            <li className="flex items-start gap-2">
              <Mail className="w-4 h-4 mt-0.5 text-[#F39C12]" />
              <a href={`mailto:${BRAND.email}`} className="hover:text-[#F39C12] break-all">
                {BRAND.email}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Phone className="w-4 h-4 mt-0.5 text-[#F39C12]" />
              <a href={`tel:${BRAND.phone}`} className="hover:text-[#F39C12]">
                {BRAND.phone}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 text-[#F39C12]" />
              <span>{BRAND.address}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-[#FFFBF5]/60">
          <p>{t("copyright")}</p>
          <p className="text-[#F39C12]">{t("with_care")}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
